import { useEffect, useRef, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  addToast,
} from "@heroui/react";
import { KeyRound, ShieldCheck } from "lucide-react";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  WebAuthnError,
} from "@simplewebauthn/browser";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeInputOtp } from "@/Components/NextBase/LimeInputOtp";
import { api } from "@/lib/api-client";
import { sentryLog } from "@/utils/sentry-utils";
import { isMobileSafari } from "react-device-detect";
import { isNativePlatform } from "@/utils/native";

type MfaReauthModalProps = {
  isOpen: boolean;
  preAuthToken: string | null;
  preAuthExpiresAt: number | null;
  isFetchingPreAuth: boolean;
  hasTotp: boolean;
  onRefreshPreAuthToken: () => Promise<void>;
  onSuccess: (accessToken: string) => void;
  onCancel: () => void;
};

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function MfaReauthModal({
  isOpen,
  preAuthToken,
  preAuthExpiresAt,
  isFetchingPreAuth,
  hasTotp,
  onRefreshPreAuthToken,
  onSuccess,
  onCancel,
}: MfaReauthModalProps) {
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const isVerifyingRef = useRef(false);

  const { mutateAsync: verifyMfa } = api.mfa.useMfaVerify();
  const { mutateAsync: recoverMfa } = api.mfa.useMfaRecovery();
  const { mutateAsync: getPasskeyOptions } =
    api.webauthn.useWebauthnAuthenticateOptions();
  const { mutateAsync: verifyPasskey } =
    api.webauthn.useWebauthnAuthenticateVerify();

  const passkeysSupported = browserSupportsWebAuthn();

  // The TOTP endpoints reject a user who never enrolled one, so a passkey-only
  // user is offered recovery codes rather than a field that can only fail.
  const showRecoveryInput = !hasTotp || useRecovery;

  const handlePasskey = async () => {
    if (isPasskeyLoading) return;

    // Without the pre-auth token the ceremony would fall back to the unbound
    // flow, which on a shared machine can return another user's session.
    if (!preAuthToken) {
      sentryLog.error("MFA re-auth passkey blocked: no pre-auth token");
      addToast({
        title: "Error",
        description: "Session data missing. Please log in again.",
        color: "danger",
      });
      return;
    }

    setIsPasskeyLoading(true);

    try {
      const { options, authenticationToken } = await getPasskeyOptions({
        preAuthToken,
      });
      const assertion = await startAuthentication({ optionsJSON: options });
      const result = await verifyPasskey({
        authenticationToken,
        response: assertion,
      });

      if (result.refreshToken) {
        localStorage.setItem("usr_refresh_token", result.refreshToken);
      }
      onSuccess(result.accessToken);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // NotAllowedError also covers a timeout and an unavailable authenticator,
      // so only an explicit abort counts as the user cancelling.
      const aborted =
        e instanceof WebAuthnError && e.code === "ERROR_CEREMONY_ABORTED";
      if (!aborted) {
        sentryLog.error("MFA re-auth passkey failed", {
          error: e instanceof Error ? e.message : String(e),
        });
        addToast({
          title: "Passkey sign-in failed",
          description:
            e?.response?.data?.error ??
            "The passkey prompt was dismissed or timed out. Please try again.",
          color: "danger",
        });
      }
      setIsPasskeyLoading(false);
      return;
    }
    setIsPasskeyLoading(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen]);

  const secondsUntilExpiry =
    preAuthExpiresAt == null
      ? null
      : Math.max(0, Math.floor((preAuthExpiresAt - nowTimestamp) / 1000));

  const isPreAuthExpired =
    secondsUntilExpiry != null ? secondsUntilExpiry <= 0 : false;

  const handleCheckStatus = async () => {
    await onRefreshPreAuthToken();
  };

  const handleSubmit = async () => {
    if (isVerifyingRef.current || isFetchingPreAuth) {
      return;
    }

    if (isPreAuthExpired) {
      addToast({
        title: "Session check required",
        description: "Re-authentication window expired. Checking status...",
        color: "warning",
      });
      await handleCheckStatus();
      return;
    }

    if (!preAuthToken) {
      sentryLog.error("MFA re-auth failed: No pre-auth token available");
      addToast({
        title: "Error",
        description: "Session data missing. Please log in again.",
        color: "danger",
      });
      await handleCheckStatus();
      return;
    }

    isVerifyingRef.current = true;
    setIsVerifying(true);

    try {
      let result: { accessToken: string; refreshToken?: string };

      if (showRecoveryInput) {
        result = await recoverMfa({
          preAuthToken,
          recoveryCode,
        });
      } else {
        result = await verifyMfa({
          preAuthToken,
          code: totpCode,
        });
      }

      if (result.refreshToken) {
        localStorage.setItem("usr_refresh_token", result.refreshToken);
      }

      addToast({
        title: "Success",
        description: "Session renewed successfully",
        color: "success",
      });

      setTotpCode("");
      setRecoveryCode("");
      onSuccess(result.accessToken);
    } catch (e: unknown) {
      const error = e as {
        response?: { status?: number; data?: { error?: string } };
      };
      const message = error?.response?.data?.error;
      const fallbackMessage = "Invalid verification code";
      const resolvedMessage =
        typeof message === "string" && message.length > 0
          ? message
          : fallbackMessage;
      const normalizedMessage = resolvedMessage.toLowerCase();
      const shouldRefreshPreAuth =
        error?.response?.status === 401 ||
        normalizedMessage.includes("expired") ||
        normalizedMessage.includes("pre-auth");

      sentryLog.error("MFA re-auth verification failed", {
        error: resolvedMessage,
      });

      if (shouldRefreshPreAuth) {
        addToast({
          title: "Session check required",
          description: "Re-authentication session expired. Checking status...",
          color: "warning",
        });
        await handleCheckStatus();
        return;
      }

      addToast({
        title: "Verification failed",
        description: resolvedMessage,
        color: "danger",
      });
    } finally {
      isVerifyingRef.current = false;
      setIsVerifying(false);
    }
  };

  const triggerSubmit = () => {
    if (isVerifyingRef.current || isFetchingPreAuth) {
      return;
    }

    void handleSubmit();
  };

  const handleClose = () => {
    setTotpCode("");
    setRecoveryCode("");
    setUseRecovery(false);
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isDismissable={false}
      hideCloseButton
      size="md"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-primary" size={24} />
            <span>Session Expired</span>
          </div>
        </ModalHeader>
        <ModalBody className="pb-6">
          {passkeysSupported && (
            <div className="flex flex-col gap-3 pb-1">
              <Button
                fullWidth
                color="primary"
                startContent={<KeyRound size={18} />}
                isLoading={isPasskeyLoading}
                onPress={() => void handlePasskey()}
                data-identifier="mfa-reauth-passkey-button"
              >
                Sign in with a passkey
              </Button>
              <div className="flex items-center gap-3">
                <div className="bg-default-200 h-px flex-1" />
                <span className="text-default-400 text-xs">or use a code</span>
                <div className="bg-default-200 h-px flex-1" />
              </div>
            </div>
          )}

          {isFetchingPreAuth ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
              <p className="text-default-500 text-sm">
                Preparing re-authentication...
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-default-500 text-sm">
                {showRecoveryInput
                  ? "Your session has expired. Enter one of your recovery codes to continue."
                  : "Your session has expired. Enter the 6-digit code from your authenticator app to continue."}
              </p>

              <div className="bg-default-100 flex items-center justify-between rounded-lg px-3 py-2">
                <p className="text-default-600 text-xs">
                  Re-auth window:{" "}
                  {secondsUntilExpiry != null
                    ? formatDuration(secondsUntilExpiry)
                    : "Unknown"}
                </p>
                <Button
                  variant="flat"
                  size="sm"
                  isLoading={isFetchingPreAuth}
                  onPress={() => {
                    void handleCheckStatus();
                  }}
                  data-identifier="mfa-reauth-check-status-button"
                >
                  Check status
                </Button>
              </div>

              {isPreAuthExpired ? (
                <p className="text-warning text-sm">
                  Re-authentication window expired. Click Check status to
                  continue.
                </p>
              ) : null}

              {showRecoveryInput ? (
                <LimeInput
                  label="Recovery code"
                  data-identifier="mfa-reauth-recovery-input"
                  size="sm"
                  className="w-full"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  autoFocus={!isMobileSafari && !isNativePlatform()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && recoveryCode.length > 0) {
                      triggerSubmit();
                    }
                  }}
                />
              ) : (
                <div className="flex justify-center">
                  <LimeInputOtp
                    length={6}
                    variant="bordered"
                    value={totpCode}
                    onChange={(val: string) => setTotpCode(val)}
                    onComplete={triggerSubmit}
                    data-identifier="mfa-reauth-totp-input"
                    autoFocus={!isMobileSafari && !isNativePlatform()}
                  />
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button
                  fullWidth
                  color="primary"
                  isLoading={isVerifying}
                  isDisabled={
                    isPreAuthExpired ||
                    isFetchingPreAuth ||
                    (showRecoveryInput
                      ? recoveryCode.length === 0
                      : totpCode.length !== 6)
                  }
                  onPress={triggerSubmit}
                  data-identifier="submit-mfa-reauth-button"
                >
                  Verify and Continue
                </Button>

                {hasTotp && (
                  <button
                    type="button"
                    className="text-primary text-sm hover:underline"
                    onClick={() => setUseRecovery(!useRecovery)}
                  >
                    {useRecovery
                      ? "Use authenticator app instead"
                      : "Use a recovery code"}
                  </button>
                )}

                <button
                  type="button"
                  className="text-default-400 text-sm hover:underline"
                  onClick={handleClose}
                >
                  Log out
                </button>
              </div>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
